import React from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  IconButton,
  alpha,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import GitHubIcon from '@mui/icons-material/GitHub';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

const ACCENT = '#137274';
const PINK = '#f50057';

const linkGroups = [
  {
    title: 'Practice',
    links: [
      { label: 'Problem Set', to: '/problems#problemset' },
      { label: 'Practice Track', to: '/practice-track' },
      { label: 'Programs', to: '/programs' },
      { label: 'Online IDE', to: '/ide' },
    ],
  },
  {
    title: 'Compete',
    links: [
      { label: 'Contests', to: '/contests' },
      { label: 'Leaderboard', to: '/leaderboard' },
      { label: '75 Hard Challenge', to: '/challenges/75-hard-coding-challenge' },
    ],
  },
  {
    title: 'Prepare',
    links: [
      { label: 'Interview Prep', to: '/interview-prep' },
      { label: '1:1 Mentorship', to: '/mentorship' },
      { label: 'Mock Interviews', to: '/mock-interview' },
      { label: 'Live Coding', to: '/live-coding' },
      { label: 'Careers', to: '/careers' },
      { label: 'Your Profile', to: '/profile' },
    ],
  },
  {
    title: 'Other Products',
    links: [{ label: 'DSA Toolkit (Extension)', to: '/dsa-toolkit' }],
  },
];

const socials = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/',
    icon: <LinkedInIcon fontSize="small" />,
  },
  {
    label: 'X',
    href: 'https://x.com/',
    icon: <XIcon fontSize="small" />,
  },
  {
    label: 'GitHub',
    href: 'https://github.com/',
    icon: <GitHubIcon fontSize="small" />,
  },
];

const FooterLink = ({ to, children }) => (
  <MuiLink
    component={RouterLink}
    to={to}
    underline="none"
    sx={{
      display: 'block',
      color: 'rgba(230, 237, 243, 0.72)',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.9,
      transition: 'color 0.15s ease',
      '&:hover': { color: '#7ed4d4' },
    }}
  >
    {children}
  </MuiLink>
);

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        flexShrink: 0,
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
        color: '#e6edf3',
        background: `
          radial-gradient(800px 280px at 8% 0%, ${alpha(ACCENT, 0.35)}, transparent 55%),
          radial-gradient(600px 240px at 92% 100%, ${alpha(PINK, 0.12)}, transparent 50%),
          linear-gradient(180deg, #121a1c 0%, #0d1214 100%)
        `,
        borderTop: `1px solid ${alpha('#7ed4d4', 0.12)}`,
      }}
    >
      <Box
        sx={{
          maxWidth: 1120,
          mx: 'auto',
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 4.5, md: 5.5 },
          pb: { xs: 3, md: 3.5 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1.3fr repeat(4, 1fr) 1fr',
            },
            gap: { xs: 3.5, md: 3 },
            mb: 4,
          }}
        >
          {/* Brand */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: 'auto' }, maxWidth: { md: 280 } }}>
            <MuiLink
              component={RouterLink}
              to="/"
              underline="none"
              sx={{ display: 'inline-block', mb: 1.25 }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.45rem',
                  letterSpacing: '-0.04em',
                  color: '#fff',
                  lineHeight: 1,
                }}
              >
                Crafter
                <Box component="span" sx={{ color: PINK }}>
                  Hack
                </Box>
              </Typography>
            </MuiLink>
            <Typography
              sx={{
                color: 'rgba(230, 237, 243, 0.65)',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                mb: 2,
                maxWidth: 300,
              }}
            >
              Practice DSA, compete in contests, pair program live, and prepare for interviews —
              all in one place.
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                border: `1px solid ${alpha('#7ed4d4', 0.2)}`,
                bgcolor: alpha(ACCENT, 0.15),
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#3fb950',
                  boxShadow: `0 0 0 3px ${alpha('#3fb950', 0.25)}`,
                }}
              />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#9fd4d5' }}>
                Built for consistent learners
              </Typography>
            </Box>
          </Box>

          {/* Link columns */}
          {linkGroups.map((group) => (
            <Box key={group.title}>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: '#7ed4d4',
                  mb: 1.25,
                }}
              >
                {group.title}
              </Typography>
              {group.links.map((link) => (
                <FooterLink key={link.to} to={link.to}>
                  {link.label}
                </FooterLink>
              ))}
            </Box>
          ))}

          {/* Connect */}
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#7ed4d4',
                mb: 1.25,
              }}
            >
              Connect
            </Typography>
            <MuiLink
              href="mailto:updates@crafterhack.com"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'rgba(230, 237, 243, 0.85)',
                fontSize: '0.875rem',
                fontWeight: 600,
                mb: 2,
                transition: 'color 0.15s ease',
                '&:hover': { color: '#7ed4d4' },
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 18 }} />
              updates@crafterhack.com
            </MuiLink>
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: 'rgba(230, 237, 243, 0.5)',
                mb: 1.25,
              }}
            >
              Follow along for contests & updates
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {socials.map((s) => (
                <IconButton
                  key={s.label}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  size="small"
                  sx={{
                    color: 'rgba(230, 237, 243, 0.75)',
                    border: `1px solid ${alpha('#fff', 0.1)}`,
                    borderRadius: 1.5,
                    bgcolor: alpha('#fff', 0.03),
                    '&:hover': {
                      color: '#7ed4d4',
                      borderColor: alpha('#7ed4d4', 0.35),
                      bgcolor: alpha(ACCENT, 0.2),
                    },
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: alpha('#fff', 0.08), mb: 2.5 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: 'rgba(230, 237, 243, 0.5)',
              fontWeight: 500,
            }}
          >
            © {year} CrafterHack. All rights reserved.
          </Typography>
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'rgba(230, 237, 243, 0.4)',
              fontWeight: 500,
            }}
          >
            Practice hard · Compete live · Interview ready
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
